import {
  COST_TYPES,
  type CostType,
  EXPENSE_PURPOSES,
  type ExpensePurpose,
  fromMinor,
  fromQty,
} from "@bt/domain";
import { useQuery } from "@tanstack/react-query";
import { DownloadIcon, PlusIcon, PowerOffIcon, ReceiptIcon, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CurrentVehicleNotice, CurrentVehicleSelect } from "@/components/app/current-vehicle";
import { EmptyState } from "@/components/app/empty-state";
import { Page, PageHeader } from "@/components/app/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Segmented, Skeleton } from "@/components/ui/misc";
import { NONE, Select } from "@/components/ui/select";
import { downloadCsv } from "@/lib/csv";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { serverToday, useFormat } from "@/lib/format";
import { useModule } from "@/lib/modules";
import { useTRPC } from "@/lib/trpc";
import {
  ExpenseMonths,
  type ExpenseRowData,
  ExpensesEmpty,
  TotalsCard,
  useExpenseActions,
  YearSelect,
} from "./expense-list";

const thisYear = () => Number(serverToday().slice(0, 4));

/**
 * The year a list starts on: this year, or the newest year with expenses when this one has none.
 */
function useYear(years: number[] | undefined) {
  const [year, setYear] = useState<number | null>(thisYear);
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (settled || !years) return;
    setSettled(true);
    if (years.length && year !== null && !years.includes(year)) setYear(years[0] ?? null);
  }, [years, settled, year]);
  return [year, setYear] as const;
}

/** A vehicle's Costs tab: totals for the year, Add expense, the list by month. */
export function VehicleCosts({ vehicleId }: { vehicleId: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const active = useModule("vehicleManagement");
  const actions = useExpenseActions();
  const { data: all } = useQuery(trpc.vehicleManagement.expenses.list.queryOptions({ vehicleId }));
  const [year, setYear] = useYear(all?.years);
  const { data: list, isLoading } = useQuery({
    ...trpc.vehicleManagement.expenses.list.queryOptions({ vehicleId, year }),
    placeholderData: (p) => p,
  });
  const { data: vehicle } = useQuery(trpc.vehicles.get.queryOptions({ id: vehicleId }));
  const add = (preset = {}) => actions.add({ vehicleId, ...preset });

  if (isLoading || !list || !all) return <Skeleton className="h-64 rounded-xl" />;
  // Nothing recorded yet: only the way in.
  if (all.years.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        {active ? (
          <ExpensesEmpty
            onAdd={add}
            title={t("vehicleManagement.emptyTitle")}
            body={t("vehicleManagement.emptyVehicle")}
          />
        ) : (
          <ModuleOff />
        )}
        {actions.sheet}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <TotalsCard
        totals={list.totals}
        title={
          year === null
            ? t("vehicleManagement.totalAllYears")
            : t("vehicleManagement.totalYear", { year })
        }
      >
        {vehicle?.totalSpent.totalMinor ? (
          <p className="flex justify-between border-t border-border pt-3 text-xs text-muted-foreground">
            <span>{t("vehicleManagement.partsUsed")}</span>
            <span className="tabular">
              {format.money(vehicle.totalSpent.totalMinor, vehicle.totalSpent.currency)}
            </span>
          </p>
        ) : null}
      </TotalsCard>
      <div className="flex items-center justify-between gap-2">
        <YearSelect years={all?.years ?? []} value={year} onChange={setYear} />
        {active ? (
          <Button onClick={() => add()}>
            <PlusIcon />
            {t("vehicleManagement.addExpense")}
          </Button>
        ) : null}
      </div>
      {list.months.length ? (
        <ExpenseMonths months={list.months} actions={actions} showVehicle={false} />
      ) : (
        <EmptyState compact icon={ReceiptIcon} title={t("vehicleManagement.noneThisYear")} />
      )}
      {actions.sheet}
    </div>
  );
}

/** Costs: every expense of the garage, narrowed by the current vehicle and the filters. */
export function CostsPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const active = useModule("vehicleManagement");
  const { data: access } = useQuery(trpc.garages.access.queryOptions());
  const { vehicleId } = useCurrentVehicle();
  const actions = useExpenseActions();
  const [purpose, setPurpose] = useState<ExpensePurpose | null>(null);
  const [costType, setCostType] = useState<CostType | null>(null);
  const [search, setSearch] = useState("");
  const { data: all } = useQuery(trpc.vehicleManagement.expenses.list.queryOptions({ vehicleId }));
  const [year, setYear] = useYear(all?.years);
  const { data: list, isLoading } = useQuery({
    ...trpc.vehicleManagement.expenses.list.queryOptions({
      vehicleId,
      year,
      purpose,
      costType,
      search: search.trim() || null,
    }),
    placeholderData: (p) => p,
  });
  const exportCsv = useExpenseCsv();

  if (access && !active) {
    return (
      <Page>
        <PageHeader title={t("vehicleManagement.costs")} />
        <ModuleOff />
      </Page>
    );
  }
  const filtered = Boolean(purpose || costType || search.trim());
  return (
    <Page>
      <PageHeader
        title={t("vehicleManagement.costs")}
        actions={
          <>
            <Button
              variant="outline"
              disabled={!list?.months.length}
              onClick={() => list && exportCsv(list.months.flatMap((m) => m.rows))}
            >
              <DownloadIcon />
              {t("vehicleManagement.exportCsv")}
            </Button>
            <Button onClick={() => actions.add({ vehicleId })}>
              <PlusIcon />
              {t("vehicleManagement.addExpense")}
            </Button>
          </>
        }
      />
      <CurrentVehicleNotice />
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <CurrentVehicleSelect className="sm:w-56" />
        <Segmented
          aria-label={t("vehicleManagement.purpose")}
          className="flex-wrap"
          value={purpose ?? "all"}
          onChange={(v) => setPurpose(v === "all" ? null : v)}
          options={[
            { value: "all", label: t("vehicleManagement.allPurposes") },
            ...EXPENSE_PURPOSES.map((p) => ({
              value: p,
              label: t(`vehicleManagement.purposes.${p}`),
            })),
          ]}
        />
        <div className="flex gap-2">
          <Select
            size="sm"
            aria-label={t("vehicleManagement.costType")}
            className="w-44"
            value={costType ?? NONE}
            onValueChange={(v) => setCostType(v === NONE ? null : v)}
            options={[
              { value: NONE, label: t("vehicleManagement.allCostTypes") },
              ...COST_TYPES.map((c) => ({
                value: c,
                label: t(`vehicleManagement.costTypes.${c}`),
              })),
            ]}
          />
          <YearSelect years={all?.years ?? []} value={year} onChange={setYear} />
        </div>
        <div className="relative sm:w-64">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            aria-label={t("common.search")}
            placeholder={t("vehicleManagement.searchPlaceholder")}
            className="pl-8"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {isLoading || !list ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <div className="flex flex-col gap-6">
          <TotalsCard
            totals={list.totals}
            title={
              year === null
                ? t("vehicleManagement.totalAllYears")
                : t("vehicleManagement.totalYear", { year })
            }
          />
          {list.months.length ? (
            <ExpenseMonths months={list.months} actions={actions} showVehicle={!vehicleId} />
          ) : filtered ? (
            <EmptyState compact icon={ReceiptIcon} title={t("vehicleManagement.noMatches")} />
          ) : (
            <ExpensesEmpty
              onAdd={(preset) => actions.add({ vehicleId, ...preset })}
              title={t("vehicleManagement.emptyTitle")}
              body={t("vehicleManagement.emptyGarage")}
            />
          )}
        </div>
      )}
      {actions.sheet}
    </Page>
  );
}

function ModuleOff() {
  const { t } = useTranslation();
  return (
    <EmptyState
      compact
      icon={PowerOffIcon}
      title={t("vehicleManagement.offTitle")}
      body={t("vehicleManagement.offBody")}
    />
  );
}

/** The rows as CSV, with the figures as the server sent them. */
function useExpenseCsv() {
  const { t } = useTranslation();
  return (rows: ExpenseRowData[]) =>
    downloadCsv(
      `${t("vehicleManagement.csvName")}-${serverToday()}`,
      [
        t("vehicleManagement.date"),
        t("vehicleManagement.vehicle"),
        t("vehicleManagement.costType"),
        t("vehicleManagement.purpose"),
        t("vehicleManagement.comment"),
        t("vehicleManagement.payee"),
        t("vehicleManagement.invoiceNumber"),
        t("vehicleManagement.amount"),
        t("vehicleManagement.currency"),
        t("vehicleManagement.quantity"),
        t("vehicleManagement.unit"),
        t("vehicleManagement.mileage"),
      ],
      rows.map((r) => [
        r.spentOn,
        r.vehicle?.name ?? t("vehicleManagement.wholeGarage"),
        t(`vehicleManagement.costTypes.${r.costType}`),
        t(`vehicleManagement.purposes.${r.purpose}`),
        r.comment,
        r.supplier?.name ?? r.payeeName,
        r.invoiceNumber,
        fromMinor(r.amountMinor, r.currency),
        r.currency,
        r.fuelQty === null ? null : fromQty(r.fuelQty),
        r.fuelUnit ? t(`vehicleManagement.fuelUnits.${r.fuelUnit}`) : null,
        r.mileage,
      ]),
    );
}
